import React from "react";
import { Box, Card, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";

import type { CSITipaltiStaging } from "../../types/CSITypes";
import { compareFields } from "./compareFields";

interface CompareLine {
  label: string;
  value: string;
  mismatch?: boolean;
}

/** Small uppercase section caption used across the expanded row. */
export function SectionCaption({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="h3"
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "text.secondary",
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );
}

function CompareSide({
  title,
  lines,
  divided,
}: {
  title: string;
  lines: CompareLine[];
  divided?: boolean;
}) {
  return (
    <Box
      sx={(theme) => ({
        p: 3.5,
        minWidth: 0,
        ...(divided && {
          borderTop: `1px solid ${theme.palette.divider}`,
          [theme.breakpoints.up("md")]: {
            borderTop: "none",
            borderLeft: `1px solid ${theme.palette.divider}`,
          },
        }),
      })}
    >
      <SectionCaption>{title}</SectionCaption>
      <Box
        component="dl"
        sx={{
          m: 0,
          display: "grid",
          gridTemplateColumns: "minmax(96px, 120px) 1fr",
          columnGap: 3,
          rowGap: 2,
          fontSize: "0.8125rem",
        }}
      >
        {lines.map((line) => (
          <React.Fragment key={line.label}>
            <Box component="dt" sx={{ color: "text.secondary" }}>
              {line.label}
            </Box>
            <Box
              component="dd"
              sx={(theme) => ({
                m: 0,
                minWidth: 0,
                overflowWrap: "anywhere",
                color: line.mismatch
                  ? theme.palette.status.warn.fg
                  : theme.palette.text.primary,
                fontWeight: line.mismatch ? 500 : 400,
              })}
            >
              {line.value}
              {line.mismatch && (
                <Box component="span" sx={visuallyHidden}>
                  {" "}
                  (differs between CSI Pay and Tipalti)
                </Box>
              )}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

export interface TipaltiCompareProps {
  row: CSITipaltiStaging;
}

/**
 * CSI Pay and Tipalti records side by side: name, email, address and payment method,
 * with the values that differ in the warn colour. The CSI Pay side also shows the
 * payee's migrated / not migrated bill counts.
 */
export function TipaltiCompare({ row }: TipaltiCompareProps) {
  const fields = compareFields(row);
  const csiLines: CompareLine[] = [
    ...fields.map((f) => ({
      label: f.label,
      value: f.csi,
      mismatch: f.mismatch,
    })),
    {
      label: "Bills",
      value: `${row.migratedBills} migrated, ${row.nonMigratedBills} not migrated`,
    },
  ];
  const tipaltiLines: CompareLine[] = fields.map((f) => ({
    label: f.label,
    value: f.tipalti,
    mismatch: f.mismatch,
  }));

  return (
    <Card
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
      }}
    >
      <CompareSide title="CSI Pay" lines={csiLines} />
      <CompareSide title="Tipalti" lines={tipaltiLines} divided />
    </Card>
  );
}

export default TipaltiCompare;
